import type { MouseEvent } from "react";
import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Badge } from "@/components/ui/badge";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import { stashListReturn } from "@/modules/shell/list-return-state";
import { relativeTimeLabel } from "@/modules/shell/relative-time";
import type { PartyRow } from "@/shared/api/parties";
import { contactRoleLabel } from "./contact-glance-math";

const columnHelper = createColumnHelper<DeskTableFeatures, PartyRow>();

/** Read-only Contacts list — edits live on detail (plan/39). */
export function contactsColumns(opts: {
  companySlug: string;
  listPath: string;
  listLabel: (priceListId: string | null) => string;
  /**
   * Desk glance peek — plain click opens peek; ⌘/Ctrl/middle-click still
   * navigates to the full detail route (plan/45).
   */
  onGlance?: (partyId: string) => void;
}) {
  function openContactLink(
    e: MouseEvent<HTMLAnchorElement>,
    partyId: string,
  ) {
    stashListReturn(opts.listPath, partyId);
    if (!opts.onGlance) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    opts.onGlance(partyId);
  }

  return columnHelper.columns([
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 220,
      cell: ({ row }) => {
        const s = row.original;
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <DocNumberLink
              to={`/c/${opts.companySlug}/contacts/${s.id}`}
              className="font-sans"
              onClick={(e) => openContactLink(e, s.id)}
            >
              {s.name}
            </DocNumberLink>
            {s.deactivatedAt != null ? (
              <Badge variant="outline">deactivated</Badge>
            ) : null}
          </div>
        );
      },
    }),
    columnHelper.accessor("code", {
      id: "code",
      header: "Code",
      size: 100,
      cell: (info) =>
        info.getValue() ? (
          <span className="font-mono text-sm">{info.getValue()}</span>
        ) : (
          <span className="text-[var(--color-muted)]">—</span>
        ),
    }),
    columnHelper.display({
      id: "roles",
      header: "Roles",
      size: 140,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-sm text-[var(--color-muted)]">
          {contactRoleLabel(row.original)}
        </span>
      ),
    }),
    columnHelper.display({
      id: "primaryPhone",
      header: "Phone",
      size: 120,
      enableSorting: false,
      cell: ({ row }) => {
        const v = row.original.primaryPhone;
        return v ? (
          <span className="font-mono text-xs tabular-nums">{v}</span>
        ) : (
          <span className="text-[var(--color-muted)]">—</span>
        );
      },
    }),
    columnHelper.display({
      id: "primaryEmail",
      header: "Email",
      size: 160,
      enableSorting: false,
      cell: ({ row }) => {
        const v = row.original.primaryEmail;
        return v ? (
          <span className="truncate text-sm">{v}</span>
        ) : (
          <span className="text-[var(--color-muted)]">—</span>
        );
      },
    }),
    columnHelper.accessor("outstanding", {
      id: "outstanding",
      header: "Due",
      size: 140,
      enableSorting: false,
      meta: { align: "right" },
      cell: ({ row }) => {
        const s = row.original;
        const ar = s.isCustomer ? Number(s.outstandingAr ?? 0) : 0;
        const ap = s.isSupplier ? Number(s.outstandingAp ?? 0) : 0;
        if (!(ar > 0) && !(ap > 0)) {
          return <span className="text-[var(--color-muted)]">—</span>;
        }
        return (
          <div className="space-y-0.5 font-mono text-xs tabular-nums">
            {ar > 0 ? (
              <div>
                {moneyDisplay(s.outstandingAr!)} ·{" "}
                <Link
                  to={`/c/${opts.companySlug}/sales/to-collect`}
                  className="no-underline"
                >
                  AR
                </Link>
              </div>
            ) : null}
            {ap > 0 ? (
              <div>
                {moneyDisplay(s.outstandingAp!)} ·{" "}
                <Link
                  to={`/c/${opts.companySlug}/purchasing/to-pay`}
                  className="no-underline"
                >
                  AP
                </Link>
              </div>
            ) : null}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "priceList",
      header: "Price list",
      size: 140,
      enableSorting: false,
      cell: ({ row }) => {
        const s = row.original;
        if (!s.isCustomer) {
          return <span className="text-[var(--color-muted)]">—</span>;
        }
        return (
          <span className="text-sm text-[var(--color-muted)]">
            {opts.listLabel(s.priceListId)}
          </span>
        );
      },
    }),
    columnHelper.accessor("updatedAt", {
      id: "updatedAt",
      header: "Updated",
      size: 88,
      enableSorting: false,
      cell: (info) => {
        const raw = info.getValue();
        return raw ? (
          <span className="text-xs text-[var(--color-muted)] tabular-nums">
            {relativeTimeLabel(raw)}
          </span>
        ) : (
          "—"
        );
      },
    }),
  ]);
}
